import { defineStore } from "pinia";
import { computed, ref } from "vue";

interface User {
  id: string;
  email: string;
  displayName: string;
  roles: ReadonlyArray<string>;
  permissions: ReadonlyArray<string>;
  /**
   * Where the credentials live. "LDAP" / "SSO" users see fewer
   * actions in the GUI (no password reset, no edit-profile) — that
   * surface is owned by FreeIPA / the IdP.
   */
  authProvider?: "LOCAL" | "LDAP" | "SSO";
}

/**
 * F-10: login/refresh-vastaus EI sisällä refreshToken-kenttää. Refresh
 * elää HttpOnly-cookiessa jota JS ei pääse lukemaan — ainoastaan
 * selain lähettää sen automaattisesti `/api/v1/auth/refresh`-
 * kutsuilla `credentials: "include"`:n kautta.
 */
interface LoginResponse {
  accessToken: string;
  accessTokenExpiresIn: number;
  user: { id: string; email: string; displayName: string };
}

const TOKEN_KEY = "web1-crm:tokens";

/**
 * F-10: localStorage:iin tallennetaan ENÄÄ vain access-token. Refresh
 * elää HttpOnly-cookiessa. XSS-haavoittuvuus ei voi enää eksfiltroida
 * pitkäikäistä refresh-tokenia — pahimmillaan vie access-tokenin
 * joka vanhenee minuuteissa.
 */
interface PersistedTokens {
  accessToken: string;
}

function readPersisted(): PersistedTokens | null {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedTokens> & {
      // Legacy-tuki: vanha localStorage-tallennus sisälsi myös
      // refreshToken-kentän, jätetään se huomiotta jos törmäämme.
      refreshToken?: string;
    };
    if (!parsed.accessToken) return null;
    return { accessToken: parsed.accessToken };
  } catch {
    return null;
  }
}

function writePersisted(tokens: PersistedTokens | null): void {
  if (tokens) {
    localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

/// Yhden-lennon mutex refresh-kutsulle. Refresh rotaatio (vanha
/// token revoked + uusi pari) tekee rinnakkaisesta retry-pyynnöstä
/// session-tappavan: backendin reuse-detection käsittelee vanhan
/// tokenin uudelleenkäytön kompromissina. Yhdellä lennossa olevalla
/// promisilla useat 401-pollut + activity-timer voivat yhtaikaa
/// odottaa samaa tulosta.
let refreshInFlight: Promise<boolean> | null = null;

export const useAuthStore = defineStore("auth", () => {
  const tokens = ref<PersistedTokens | null>(readPersisted());
  const user = ref<User | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  /// Access-tokenin viimeisin tunnettu vanhenemis-aika (Date.now()-ms).
  /// Asetetaan login + refresh -vastauksen `accessTokenExpiresIn`:stä.
  /// Refresh-strategia luotaa tähän: refresh sallitaan kun jäljellä
  /// alle REFRESH_THRESHOLD_MS. Null = vanhentumisaika tuntematon
  /// (esim. juuri ladattu localStoragesta restartilla) → seuraava
  /// authFetch joka osuu 401:ee triggeröi refresh-retryn.
  const accessTokenExpiresAt = ref<number | null>(null);

  /// Käyttäjän viimeinen interaktion aikaleima (mousemove, click,
  /// keydown, scroll throttlattu — ks. composables/useSessionRefresh).
  /// Activity-gate: refreshataan vain jos käyttäjä oli aktiivinen
  /// viime aikoina, muuten toimeton sessio saa vanheta luonnostaan.
  const lastActivityAt = ref<number>(Date.now());

  function markActivity(): void {
    lastActivityAt.value = Date.now();
  }

  const isAuthenticated = computed(() => Boolean(tokens.value?.accessToken));

  async function login(email: string, password: string): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const res = await fetch("/api/v1/auth/login", {
        // GUI calls all API endpoints under /api/v1/...; the NestJS
        // global prefix in apps/api/src/main.ts maps controller paths
        // accordingly.
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // F-10: credentials:"include" jotta vastaukseksi tullut
        // Set-Cookie (refresh-cookie) tallentuu cross-subdomain
        // -tilanteessakin (`crm.web1.fi` → `crm-api.web1.fi`).
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          message?: string;
        };
        throw new Error(body.message ?? `Login failed (${res.status})`);
      }
      const data = (await res.json()) as LoginResponse;
      tokens.value = { accessToken: data.accessToken };
      // `expiresIn` on sekuntimäärä joka API:n DTO:ssa (auth.controller
      // swagger-example näyttää 900 = 15 min). Käännetään absoluuttiseen
      // ms-aikaleimaan jota refresh-strategia voi vertailla `Date.now()`:in
      // kanssa ilman ajan kulumisen mukana ajelevaa "jäljellä N sekuntia"-
      // laskentaa.
      accessTokenExpiresAt.value =
        Date.now() + data.accessTokenExpiresIn * 1000;
      writePersisted(tokens.value);
      lastActivityAt.value = Date.now();
      await fetchMe();
    } catch (err) {
      error.value = (err as Error).message;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchMe(): Promise<void> {
    if (!tokens.value) return;
    // Snapshot the token at call-time so a logout/change-password
    // racing with an in-flight /me request can't repopulate `user`
    // after the session was deliberately cleared.
    const tokenAtStart = tokens.value.accessToken;
    try {
      const res = await fetch("/api/v1/auth/me", {
        headers: { Authorization: `Bearer ${tokenAtStart}` },
      });
      // If the token changed (or was cleared) while we were waiting
      // for /me to respond, drop the result on the floor — the
      // session in scope when we fired the request is no longer
      // the current one.
      if (tokens.value?.accessToken !== tokenAtStart) return;
      if (res.ok) {
        user.value = (await res.json()) as User;
      } else if (res.status === 401) {
        logout();
      }
    } catch {
      // Network blip during init — leave `user` null. The next
      // authFetch will trigger handleUnauthenticated on 401 if the
      // token has actually expired; if it was a transient error,
      // the user can keep working off whatever cached data is in
      // memory and the next interaction will refetch.
    }
  }

  /**
   * Vaihda nykyinen refresh-token uuteen access-tokeniin. F-10:n
   * jälkeen refresh-token elää HttpOnly-cookiessa — JS ei lue eikä
   * lähetä sitä eksplisiittisesti; `credentials: "include"` kertoo
   * selaimelle että cookie pitää liittää pyyntöön.
   *
   * Käytetään silent-refresh:nä taustalla ennen kun access-token
   * vanhenee, sekä yhden retry-yrityksen sisällä `authFetch`:n
   * 401-polulla.
   *
   * Yhden-lennon mutex (`refreshInFlight`) varmistaa että usean
   * rinnakkaisen authFetch:n 401-polku ei laukaise montaa refreshia
   * peräkkäin — backend rotaatiosta seuraisi "previous refresh
   * token revoked"-virhe ja koko sessio menisi nurin.
   *
   * Palauttaa `true` jos refresh onnistui, `false` jos epäonnistui.
   * Epäonnistunut refresh KUTSUU logout:n itse (ei jätä storesta
   * vanhentuneita tokeneita).
   */
  async function refresh(): Promise<boolean> {
    // Jos toinen refresh on jo lennossa, odotetaan sen tulosta sen
    // sijaan että sytytettäisiin oma rinnalle.
    if (refreshInFlight) return refreshInFlight;
    refreshInFlight = (async () => {
      try {
        const res = await fetch("/api/v1/auth/refresh", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // F-10: refresh-token lähtee selaimen cookie-jarista; JS:n
          // ei tarvitse (eikä voi) lukea sitä.
          credentials: "include",
          // Body on tyhjä — backend lukee tokenin cookiesta.
          body: JSON.stringify({}),
        });
        if (!res.ok) {
          // Refresh-token revoked/expired/reused → backendin reuse-
          // detection on ehkä nuked koko session-perheen. Logataan
          // local-stateen sama: kirjaudutaan ulos siististi.
          logout();
          return false;
        }
        const data = (await res.json()) as LoginResponse;
        tokens.value = { accessToken: data.accessToken };
        accessTokenExpiresAt.value =
          Date.now() + data.accessTokenExpiresIn * 1000;
        writePersisted(tokens.value);
        return true;
      } catch {
        // Verkko-blip jne. — älä logataa ulos, antaa seuraavan
        // yrityksen yrittää uudestaan.
        return false;
      } finally {
        refreshInFlight = null;
      }
    })();
    return refreshInFlight;
  }

  function logout(): void {
    // Fire-and-forget backend-kutsu joka revoke:aa refresh-tokenin ja
    // tyhjentää HttpOnly-cookien (`Set-Cookie: refresh=; Max-Age=0`).
    // Älä odota — UI:n ei pidä jäädä jumiin verkkoblipistä.
    if (tokens.value?.accessToken) {
      const accessToken = tokens.value.accessToken;
      // Käytetään try/catch + Promise.resolve-kierrosta jotta jos
      // `fetch` on mockattu palauttamaan undefined (kuten testeissä),
      // .catch ei kaada koko logoutia.
      try {
        const p = fetch("/api/v1/auth/logout", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: "include",
          body: JSON.stringify({}),
        });
        if (p && typeof p.catch === "function") {
          void p.catch(() => {
            // Best-effort. Cookie poistetaan local-statesta joka tapauksessa.
          });
        }
      } catch {
        // Sync-virheet (esim. fetch on stubattu poispäin) eivät saa
        // estää local-statelogoutia.
      }
    }
    tokens.value = null;
    user.value = null;
    accessTokenExpiresAt.value = null;
    writePersisted(null);
  }

  /**
   * Self-service: trigger an email-link reset. The API always returns
   * 204 (it doesn't reveal whether the email exists), so this resolves
   * with `void` on any non-network failure too — surfacing API-shaped
   * errors here would defeat the API's anti-enumeration design.
   */
  async function forgotPassword(email: string): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      await fetch("/api/v1/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
    } finally {
      loading.value = false;
    }
  }

  /**
   * Redeem a PASSWORD_RESET token. Surfaces API errors (token expired,
   * password policy) so the view can show them inline.
   */
  async function resetPassword(
    token: string,
    newPassword: string,
  ): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const res = await fetch("/api/v1/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          message?: string;
        };
        const message = body.message ?? `Reset failed (${res.status})`;
        throw new Error(message);
      }
    } catch (err) {
      error.value = (err as Error).message;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Logged-in self-service password change. On success, the API has
   * revoked every refresh token for this user — we mirror that locally
   * by clearing in-memory tokens so the next route guard kicks the
   * user back to /login. Surfaces API errors (wrong current password,
   * policy, history) so the view can show them inline.
   */
  async function changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<void> {
    if (!tokens.value) {
      throw new Error("Not authenticated");
    }
    loading.value = true;
    error.value = null;
    try {
      const res = await fetch("/api/v1/auth/change-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tokens.value.accessToken}`,
        },
        // Backend tyhjentää refresh-cookien onnistumisen yhteydessä.
        credentials: "include",
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          message?: string;
        };
        const message = body.message ?? `Change failed (${res.status})`;
        throw new Error(message);
      }
      // API revoked every refresh token + cleared cookie; mirror
      // locally so the next /me / refresh attempt kicks the user
      // back to /login.
      tokens.value = null;
      user.value = null;
      accessTokenExpiresAt.value = null;
      writePersisted(null);
    } catch (err) {
      error.value = (err as Error).message;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  return {
    tokens,
    user,
    loading,
    error,
    accessTokenExpiresAt,
    lastActivityAt,
    isAuthenticated,
    login,
    fetchMe,
    refresh,
    markActivity,
    logout,
    forgotPassword,
    resetPassword,
    changePassword,
  };
});
